// SD57 / FR-AD7 operator console: media processing. GET/PUT /ops/media.
//
// Two platform-wide kinds of video work, both off by default: muxing (FR-P12's packet-copy
// remux into HLS segments) and transcoding (FR-P3's quality ladder, plus the one-rung encode a
// non-H.264 recording needs before it can be muxed). Each has an on/off switch, a mode
// (automatic after publish, or on demand — FR-O13's permission still decides who may ask) and
// its options. The audio track and poster are not processing in this sense and always run.
// The server applies a save to the next job on every pod — no redeploy.
import { useEffect, useState, type FormEvent } from "react";
import { Badge, Card, SegmentedControl, Select, ToggleRow } from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import type { OpsMediaSettingsOut, ProcessingMode } from "../api/types";
import { Button, Field, Notice } from "../components/ui";
import {
  bytesToMbps,
  describeMux,
  describeTranscode,
  NO_RUNGS_MESSAGE,
  toPut,
  validateMedia,
  type MediaFormErrors,
  type MediaFormValues,
} from "./mediaForm";
import "../admin/admin.css";

const MODE_OPTIONS: { value: ProcessingMode; label: string }[] = [
  { value: "on_demand", label: "On demand" },
  { value: "automatic", label: "Automatic" },
];

// Concurrency is a segmented pill of 1..max while that stays short (the server's max is the
// ladder length, 4 today); past this many options it falls back to a number field.
const MAX_SEGMENTED_CONCURRENCY = 8;

function StatusBadge({ enabled, mode }: { enabled: boolean; mode: ProcessingMode }) {
  return (
    <Badge size="sm" tone={enabled ? "shared" : "neutral"}>
      {enabled ? (mode === "automatic" ? "Automatic" : "On demand") : "Off"}
    </Badge>
  );
}

/** The mode pill with its plain-language summary as help text under it. */
function ModeField({
  label,
  value,
  onChange,
  summary,
  testId,
}: {
  label: string;
  value: ProcessingMode;
  onChange: (mode: ProcessingMode) => void;
  summary: string;
  testId: string;
}) {
  return (
    <div className="gk-field">
      <span className="gk-field__label">Mode</span>
      <SegmentedControl
        className="ops-seg"
        label={label}
        options={MODE_OPTIONS}
        value={value}
        onChange={(v) => onChange(v as ProcessingMode)}
      />
      <p className="ops-media-summary" data-testid={testId}>
        {summary}
      </p>
    </div>
  );
}

function valuesFrom(cfg: OpsMediaSettingsOut): MediaFormValues {
  return {
    muxEnabled: cfg.mux.enabled,
    muxMode: cfg.mux.mode,
    autoBudgetSecs: String(cfg.mux.auto_budget_secs),
    throughputMbps: bytesToMbps(cfg.mux.throughput_bytes_per_sec),
    overheadSecs: String(cfg.mux.overhead_secs),
    transcodeEnabled: cfg.transcode.enabled,
    transcodeMode: cfg.transcode.mode,
    rungs: cfg.transcode.rungs,
    preset: cfg.transcode.preset,
    rungConcurrency: String(cfg.transcode.rung_concurrency),
  };
}

export default function OpsMediaPage() {
  const [cfg, setCfg] = useState<OpsMediaSettingsOut | null>(null);
  const [values, setValues] = useState<MediaFormValues | null>(null);
  const [errors, setErrors] = useState<MediaFormErrors>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = (next: OpsMediaSettingsOut) => {
    setCfg(next);
    setValues(valuesFrom(next));
    setErrors({});
  };

  useEffect(() => {
    api<OpsMediaSettingsOut>("GET", "/ops/media")
      .then(apply)
      .catch((err) => setLoadError(err instanceof ApiFail ? err.message : "Couldn't load media settings."));
  }, []);

  const set = <K extends keyof MediaFormValues>(key: K, value: MediaFormValues[K]) => {
    setValues((prev) => (prev ? { ...prev, [key]: value } : prev));
    setSaved(false);
  };

  function toggleRung(rung: string) {
    if (!values || !cfg) return;
    const has = values.rungs.includes(rung);
    // Keep the ladder's own order (highest first) whatever order they were clicked in.
    const next = cfg.options.rungs.filter((r) => (r === rung ? !has : values.rungs.includes(r)));
    set("rungs", next);
  }

  const maxConc = cfg?.options.max_rung_concurrency ?? 1;
  // The empty-ladder message shows as soon as the last level is unticked, not only on save.
  const noRungs = values?.rungs.length === 0;

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!values || !cfg) return;
    const found = validateMedia(values, cfg.options.max_rung_concurrency);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      apply(await api<OpsMediaSettingsOut>("PUT", "/ops/media", toPut(values)));
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't save media settings.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Media processing</h1>
          <p>
            Whether Grackle muxes recordings into streaming segments and whether it transcodes them into extra
            quality levels — automatically after publish, or only when someone asks. Both are off by default. The
            audio track and poster are always produced.
          </p>
        </div>
      </header>
      {loadError ? <Notice tone="error">{loadError}</Notice> : null}
      {!cfg && !loadError ? <div className="gk-sm">Loading media settings…</div> : null}
      {cfg && values ? (
        <form onSubmit={onSave} className="ops-form" noValidate>
          <Card title="Muxing" action={<StatusBadge enabled={values.muxEnabled} mode={values.muxMode} />}>
            <div className="ops-media-card">
              <ToggleRow
                label="Allow muxing"
                description="Packet-copy a recording into seekable HLS segments. No video is re-encoded."
                checked={values.muxEnabled}
                onChange={() => set("muxEnabled", !values.muxEnabled)}
              />
              <ModeField
                label="Muxing mode"
                value={values.muxMode}
                onChange={(v) => set("muxMode", v)}
                summary={describeMux(values)}
                testId="mux-summary"
              />
              <div className="ops-media-grid ops-media-options">
                <Field
                  label="Automatic budget (seconds)"
                  className="mono"
                  inputMode="numeric"
                  value={values.autoBudgetSecs}
                  onChange={(e) => set("autoBudgetSecs", e.target.value)}
                  help="Automatic mode only: recordings projected to take longer than this wait to be asked for."
                  error={errors.autoBudgetSecs}
                />
                <Field
                  label="Worker throughput (MB/s)"
                  className="mono"
                  inputMode="decimal"
                  value={values.throughputMbps}
                  onChange={(e) => set("throughputMbps", e.target.value)}
                  help="Download + copy + upload rate used for the projection."
                  error={errors.throughputMbps}
                />
                <Field
                  label="Fixed overhead (seconds)"
                  className="mono"
                  inputMode="numeric"
                  value={values.overheadSecs}
                  onChange={(e) => set("overheadSecs", e.target.value)}
                  help="The part of a remux that doesn't scale with size."
                  error={errors.overheadSecs}
                />
              </div>
            </div>
          </Card>

          <Card
            title="Transcoding"
            action={<StatusBadge enabled={values.transcodeEnabled} mode={values.transcodeMode} />}
          >
            <div className="ops-media-card">
              <ToggleRow
                label="Allow transcoding"
                description="Re-encode video: the quality ladder, and the one-off encode a non-H.264 recording needs to be muxed."
                checked={values.transcodeEnabled}
                onChange={() => set("transcodeEnabled", !values.transcodeEnabled)}
              />
              <ModeField
                label="Transcoding mode"
                value={values.transcodeMode}
                onChange={(v) => set("transcodeMode", v)}
                summary={describeTranscode(values)}
                testId="transcode-summary"
              />
              <div className="ops-media-card ops-media-options">
                <div className="gk-field">
                  <span className="gk-field__label">Quality levels</span>
                  {/* The checkbox alone shows what's selected; the chip looks the same either way. */}
                  <div className="ops-chips">
                    {cfg.options.rungs.map((rung) => (
                      <label key={rung} className="ops-chip">
                        <input
                          type="checkbox"
                          checked={values.rungs.includes(rung)}
                          onChange={() => toggleRung(rung)}
                        />
                        {rung}
                      </label>
                    ))}
                  </div>
                  <div className={"gk-field__help" + (noRungs ? " gk-field__help--error" : "")}>
                    {noRungs
                      ? NO_RUNGS_MESSAGE
                      : "Each level is produced only when the recording is at least that tall; never upscaled."}
                  </div>
                </div>
                <div className="ops-media-pair">
                  <div className="gk-field">
                    <Select
                      label="Encoder preset"
                      options={cfg.options.presets.map((p) => ({ value: p, label: p }))}
                      value={values.preset}
                      onChange={(v) => set("preset", v)}
                    />
                    <div className="gk-field__help">
                      x264 speed/size trade-off. Faster presets use less CPU, larger files.
                    </div>
                  </div>
                  {maxConc <= MAX_SEGMENTED_CONCURRENCY ? (
                    <div className="gk-field">
                      <span className="gk-field__label">Levels encoded at once</span>
                      <SegmentedControl
                        className="ops-seg ops-seg--count"
                        label="Levels encoded at once"
                        options={Array.from({ length: maxConc }, (_, i) => String(i + 1))}
                        value={values.rungConcurrency.trim()}
                        onChange={(v) => set("rungConcurrency", v)}
                      />
                      <div className={"gk-field__help" + (errors.rungConcurrency ? " gk-field__help--error" : "")}>
                        {errors.rungConcurrency ?? "Lower spreads the CPU load of one job over more time."}
                      </div>
                    </div>
                  ) : (
                    <Field
                      label="Levels encoded at once"
                      className="mono"
                      inputMode="numeric"
                      value={values.rungConcurrency}
                      onChange={(e) => set("rungConcurrency", e.target.value)}
                      help={`1–${maxConc}. Lower spreads the CPU load of one job over more time.`}
                      error={errors.rungConcurrency}
                    />
                  )}
                </div>
              </div>
            </div>
          </Card>

          {error ? <Notice tone="error">{error}</Notice> : null}
          {saved ? <Notice>Saved. The next processing job uses these settings.</Notice> : null}
          <div className="ops-media-save">
            <Button type="submit" loading={saving}>
              Save media settings
            </Button>
            <span className="ops-media-save__note">
              {cfg.source === "console" && cfg.updated_at
                ? `Last changed ${new Date(cfg.updated_at).toLocaleString()}${
                    cfg.updated_by_email ? ` by ${cfg.updated_by_email}` : ""
                  }`
                : "Built-in defaults — nothing saved yet."}
            </span>
          </div>
        </form>
      ) : null}
    </>
  );
}
